import { useCallback, useState } from "react";
import type { ReceiptSource, ReconciliationDefaults } from "@/shared/api";
import { useCompleteValues, validateSettings } from "./formValidation";
import { useBbWcRates } from "./useBbWcRates";
import { useRateBands } from "./useRateBands";
import type { UploadFormValues } from "./UploadForm.types";

/** Today, as yyyy-mm-dd, for seeding the dated-rate rows. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

interface Options {
  defaults: ReconciliationDefaults;
  onSubmit: (values: UploadFormValues) => void;
}

/**
 * Form state for the two uploads and the run settings.
 *
 * Both files are held here rather than inside the dropzones, so the submit
 * button can tell whether the form is complete without reaching into children.
 *
 * The Boot Barn wholesale toggles and rates live in {@link useBbWcRates}, and
 * the completeness rule itself in {@link ./formValidation.ts} - this hook only
 * wires the two together, which is what keeps it small enough to read at a
 * glance.
 */
export function useUploadForm({ defaults, onSubmit }: Options) {
  const [wcReport, setWcReport] = useState<File | null>(null);
  // Always starts on the upload, on every deployment. The feed is the newer and
  // less proven of the two, and a default that silently changes where the
  // numbers come from is not a default anybody asked for.
  const [wcSource, setWcSource] = useState<ReceiptSource>("upload");
  const [opsMaster, setOpsMaster] = useState<File | null>(null);
  const [landingRate, setLandingRate] = useState(String(defaults.landing_rate));
  const [lookbackDays, setLookbackDays] = useState(String(defaults.ops_lookback_days));
  const bands = useRateBands(today());
  const bbWc = useBbWcRates(today());

  const {
    rate: parsedRate,
    lookback: parsedLookback,
    rateError,
    lookbackError,
  } = validateSettings(landingRate, lookbackDays);

  const values = useCompleteValues({
    wcReport,
    wcSource,
    opsMaster,
    parsedRate,
    parsedLookback,
    rateError,
    lookbackError,
    bands: bands.bands,
    bbWc,
  });

  const submit = useCallback(() => {
    if (values) onSubmit(values);
  }, [values, onSubmit]);

  return {
    wcReport,
    wcSource,
    opsMaster,
    landingRate,
    lookbackDays,
    rateError,
    lookbackError,
    canSubmit: values !== null,
    bands,
    // Every BB WC field and setter, under the exact names UploadForm.tsx wires
    // to the view: bbWcRateFallback, setBbWcLandingRate, bbWcBands, and so on.
    ...bbWc,
    setWcReport,
    setWcSource,
    setOpsMaster,
    setLandingRate,
    setLookbackDays,
    clearWcReport: useCallback(() => setWcReport(null), []),
    clearOpsMaster: useCallback(() => setOpsMaster(null), []),
    submit,
  };
}
